import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Play,
  Square,
  RefreshCw,
  ExternalLink,
  Share2,
  Trash2,
  Loader2,
  Clock,
  Lock,
  Users,
  UserCheck,
  GitBranch,
  Calendar,
  Activity,
  Copy,
  Check,
  Server,
  Cpu,
  HardDrive,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  X,
  Rocket,
  Globe,
  Pencil,
  Key,
} from 'lucide-react'
import FileBrowserPanel from '../components/workspace/FileBrowserPanel'
import SessionsPanel from '../components/SessionsPanel'
import EditAppInfoModal from '../components/EditAppInfoModal'
import { Switch } from '../components/ui/Switch'
import {
  getApp as fetchApp,
  startApp,
  stopApp,
  rebuildApp,
  deleteApp,
  updateApp,
  listAppShares,
  shareApp,
  removeAppShare,
  shareWithPlatform,
  unshareFromPlatform,
  getAppResources,
  resetAppResources,
  getAppLogs,
  getDeploymentStatus,
  triggerProductionBuild,
  stopProductionBuild,
  getProductionLogs,
  listAppSecrets,
  upsertAppSecret,
  deleteAppSecret,
  type App,
  type AppShare,
  type AppSecretMetadata,
  type MergedAppResources,
  type DeploymentStatus,
} from '../lib/api'
import { useUser } from '../hooks/useUser'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { HelpLink } from '../components/HelpLink'
import AppUsageStats from '../components/analytics/AppUsageStats'
import VisitorTrendChart from '../components/analytics/VisitorTrendChart'
import InlineCategoryPicker from '../components/InlineCategoryPicker'
import { withMountBase } from '../lib/mountBase'

const statusConfig: Record<string, { color: string; textColor: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', textColor: 'text-green-500', label: 'Running', pulse: false },
  stopped: { color: 'bg-gray-500', textColor: 'text-gray-400', label: 'Stopped', pulse: false },
  queued: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Queued', pulse: true },
  building: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Building...', pulse: true },
  installing: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Installing Dependencies...', pulse: true },
  starting: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Starting...', pulse: true },
  error: { color: 'bg-red-500', textColor: 'text-red-500', label: 'Error', pulse: false },
}

const deploymentSteps = ['queued', 'building', 'installing', 'starting', 'running'] as const

const statusDescriptions: Record<string, string> = {
  queued: 'Your app is queued for deployment',
  building: 'Setting up the container environment',
  installing: 'Installing dependencies (npm install / pip install)',
  starting: 'Starting your app process',
  running: 'Your app is live and accessible',
  stopped: 'App is not running',
}

const accessConfig = {
  personal: { icon: Lock, label: 'Personal', description: 'Only you can access' },
  specific: { icon: UserCheck, label: 'Specific Users', description: 'Shared with selected users' },
  platform: { icon: Users, label: 'Platform', description: 'All platform users can access' },
}

const runtimeConfig = {
  node: { label: 'Node.js', color: 'bg-green-500' },
  python: { label: 'Python', color: 'bg-blue-500' },
  static: { label: 'Static', color: 'bg-purple-500' },
  auto: { label: 'Auto', color: 'bg-gray-500' },
}

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function AppDetail() {
  const { appName } = useParams<{ appName: string }>()
  const navigate = useNavigate()
  const user = useUser()

  const [app, setApp] = useState<App | null>(null)
  const [shares, setShares] = useState<AppShare[]>([])
  const [resources, setResources] = useState<MergedAppResources | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showResources, setShowResources] = useState(false)
  const [copied, setCopied] = useState(false)
  const [logs, setLogs] = useState<string>('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsExpanded, setLogsExpanded] = useState(false)
  const [logTab, setLogTab] = useState<'all' | 'build' | 'runtime'>('all')
  const [actionError, setActionError] = useState<string | null>(null)
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [deploymentPhase, setDeploymentPhase] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<{
    log_excerpt?: string; port?: number; resolution?: string
  } | null>(null)

  // Production build state
  const [productionStatus, setProductionStatus] = useState<string | null>(null)
  const [productionError, setProductionError] = useState<string | null>(null)
  const [productionLogs, setProductionLogs] = useState<{ build_logs: string; runtime_logs: string } | null>(null)
  const [productionLogsExpanded, setProductionLogsExpanded] = useState(false)
  const [productionLogsLoading, setProductionLogsLoading] = useState(false)
  const [copiedProdUrl, setCopiedProdUrl] = useState(false)
  const [copiedDevUrl, setCopiedDevUrl] = useState(false)
  const [appSecrets, setAppSecrets] = useState<AppSecretMetadata[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [secretSaving, setSecretSaving] = useState(false)
  const [secretDeleting, setSecretDeleting] = useState<string | null>(null)
  const [secretError, setSecretError] = useState<string | null>(null)
  const [secretSuccess, setSecretSuccess] = useState<string | null>(null)
  const [secretNameInput, setSecretNameInput] = useState('')
  const [secretValueInput, setSecretValueInput] = useState('')
  const [secretDescriptionInput, setSecretDescriptionInput] = useState('')

  const loadApp = useCallback(async () => {
    if (!appName) return
    try {
      const found = await fetchApp(appName)
      setApp(found)
      const [sharesData, resourcesData] = await Promise.all([
        listAppShares(appName).catch(() => []),
        getAppResources(appName).catch(() => null),
      ])
      setShares(sharesData)
      if (resourcesData) setResources(resourcesData)
    } catch (err) {
      console.error('Failed to load app:', err)
      setApp(null)
    } finally {
      setLoading(false)
    }
  }, [appName])

  useEffect(() => {
    loadApp()
  }, [loadApp])

  // Redirect view-only users to the app URL
  useEffect(() => {
    if (!app || loading) return
    const myPermission = app.my_permission ?? 'owner'
    const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)
    if (!canEdit && app.url) {
      window.location.href = app.url
    }
  }, [app, loading])

  // Compute the display phase: prefer polled phase > app.deployment_phase > app.status
  const displayPhase = deploymentPhase || app?.deployment_phase || app?.status || 'stopped'
  const isDeploying = ['queued', 'building', 'installing', 'starting'].includes(displayPhase)

  // Compute production display status
  const prodStatus = productionStatus || app?.production_status || null
  const isProductionBuilding = prodStatus === 'building'

  // Poll deployment status during active deployment or production build
  const pollDeploymentStatus = useCallback(async () => {
    if (!appName) return
    try {
      const result: DeploymentStatus = await getDeploymentStatus(appName)
      setDeploymentPhase(result.deployment_phase)
      setErrorMessage(result.error_message)
      setErrorDetails(result.error_details)

      // Update production status from deployment-status response
      if (result.production_status !== undefined) {
        setProductionStatus(result.production_status ?? null)
        setProductionError(result.production_error_message ?? null)
      }

      // When deployment settles, refresh full app data and stop polling
      if (result.deployment_phase === 'running' || result.deployment_phase === 'error') {
        await loadApp()
        // Keep the phase set so UI shows final state until next action
      }

      // When production build settles, refresh full app data
      if (result.production_status && result.production_status !== 'building') {
        await loadApp()
      }
    } catch (err) {
      console.error('Failed to poll deployment status:', err)
      throw err
    }
  }, [appName, loadApp])

  useAutoRefresh(pollDeploymentStatus, {
    baseInterval: 2000,
    maxInterval: 5000,
    enabled: isDeploying || isProductionBuilding,
  })

  // Auto-expand logs on error and switch to build tab
  useEffect(() => {
    if (displayPhase === 'error') {
      setLogsExpanded(true)
      setLogTab('build')
    }
  }, [displayPhase])

  // Fetch logs (supports log type tabs)
  const fetchLogs = useCallback(async () => {
    if (!appName || !app) return
    // Allow log fetch when running, error, or during deployment
    const canFetchLogs = app.status === 'running' || displayPhase === 'error' || isDeploying
    if (!canFetchLogs) return
    setLogsLoading(true)
    try {
      const result = await getAppLogs(appName, 100, logTab)
      setLogs(result.logs || '')
    } catch (err) {
      console.error('Failed to fetch logs:', err)
      setLogs('')
      throw err  // Allow useAutoRefresh to detect the error for backoff
    } finally {
      setLogsLoading(false)
    }
  }, [appName, app, logTab, displayPhase, isDeploying])

  // Re-fetch logs when tab changes
  useEffect(() => {
    if (logsExpanded && app) {
      fetchLogs()
    }
  }, [logTab, logsExpanded, fetchLogs, app])

  // Initial log fetch when conditions become true
  useEffect(() => {
    if ((app?.status === 'running' || displayPhase === 'error') && logsExpanded) {
      fetchLogs()
    }
  }, [app?.status, displayPhase, logsExpanded, fetchLogs])

  // Log polling with exponential backoff
  useAutoRefresh(fetchLogs, {
    baseInterval: 5000,
    maxInterval: 60000,   // 1 minute max backoff
    enabled: (app?.status === 'running' || displayPhase === 'error') && logsExpanded,
  })

  async function handleStart() {
    if (!app) return
    setActionLoading('start')
    setActionError(null)
    // Immediately show deployment progression before the blocking API call
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await startApp(app.name)
      // Use the final phase from the server (may be 'running' or 'error')
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to start app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStop() {
    if (!app) return
    setActionLoading('stop')
    setActionError(null)
    try {
      await stopApp(app.name)
      // Clear deployment state so polling stops and UI resets
      setDeploymentPhase(null)
      setErrorMessage(null)
      setErrorDetails(null)
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleRebuild() {
    if (!app) return
    setActionLoading('rebuild')
    setActionError(null)
    // Immediately show deployment progression before the blocking API call
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await rebuildApp(app.name)
      // Use the final phase from the server (may be 'running' or 'error')
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to rebuild app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete() {
    if (!app) return
    setActionLoading('delete')
    try {
      await deleteApp(app.name)
      navigate('/apps')
    } catch (err) {
      console.error('Failed to delete app:', err)
      setActionLoading(null)
    }
  }

  async function handleProductionBuild() {
    if (!app) return
    setActionLoading('production-build')
    setActionError(null)
    setProductionStatus('building')
    setProductionError(null)
    try {
      await triggerProductionBuild(app.name)
      await loadApp()
    } catch (err) {
      setProductionStatus(null)
      setActionError(err instanceof Error ? err.message : 'Failed to start production build')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStopProduction() {
    if (!app) return
    setActionLoading('production-stop')
    setActionError(null)
    try {
      await stopProductionBuild(app.name)
      setProductionStatus('stopped')
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop production')
    } finally {
      setActionLoading(null)
    }
  }

  async function fetchProductionLogs() {
    if (!appName) return
    setProductionLogsLoading(true)
    try {
      const result = await getProductionLogs(appName)
      setProductionLogs({ build_logs: result.build_logs, runtime_logs: result.runtime_logs })
    } catch (err) {
      console.error('Failed to fetch production logs:', err)
      setProductionLogs(null)
    } finally {
      setProductionLogsLoading(false)
    }
  }

  function copyUrl() {
    if (!app) return
    navigator.clipboard.writeText(app.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function copyProductionUrl() {
    if (!app?.production_url) return
    navigator.clipboard.writeText(app.production_url)
    setCopiedProdUrl(true)
    setTimeout(() => setCopiedProdUrl(false), 2000)
  }

  function copyDevPreviewUrl() {
    if (!app?.dev_preview_url) return
    navigator.clipboard.writeText(app.dev_preview_url)
    setCopiedDevUrl(true)
    setTimeout(() => setCopiedDevUrl(false), 2000)
  }

  const loadSecrets = useCallback(async () => {
    if (!app) return
    const isOwner = app.is_owner ?? true
    const canManage = isOwner || user.isAdmin
    if (!canManage) {
      setAppSecrets([])
      return
    }

    setSecretsLoading(true)
    setSecretError(null)
    try {
      const result = await listAppSecrets(app.name)
      setAppSecrets(result.secrets || [])
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to load secrets')
    } finally {
      setSecretsLoading(false)
    }
  }, [app, user.isAdmin])

  useEffect(() => {
    setSecretSuccess(null)
  }, [appName])

  useEffect(() => {
    if (app) {
      loadSecrets()
    }
  }, [app, loadSecrets])

  async function handleSaveSecret() {
    if (!app) return
    const key = secretNameInput.trim()
    if (!key || !secretValueInput) {
      setSecretError('Secret name and value are required')
      return
    }

    setSecretSaving(true)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(app.name, key, {
        value: secretValueInput,
        description: secretDescriptionInput.trim() || undefined,
      })
      setSecretSuccess(`Secret "${key}" saved`)
      setSecretValueInput('')
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to save secret')
    } finally {
      setSecretSaving(false)
    }
  }

  async function handleDeleteSecret(secretName: string) {
    if (!app) return
    const confirmed = window.confirm(`Delete secret "${secretName}"?`)
    if (!confirmed) return

    setSecretDeleting(secretName)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await deleteAppSecret(app.name, secretName)
      setSecretSuccess(`Secret "${secretName}" deleted`)
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to delete secret')
    } finally {
      setSecretDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!app) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold mb-2">App not found</h2>
        <p className="text-muted-foreground mb-4">The app "{appName}" doesn't exist or you don't have access.</p>
        <Link to="/apps" className="text-primary hover:underline">
          Back to My Apps
        </Link>
      </div>
    )
  }

  const status = statusConfig[displayPhase] || statusConfig.stopped
  const access = accessConfig[app.access_level as keyof typeof accessConfig] || accessConfig.personal
  const runtime = runtimeConfig[app.runtime as keyof typeof runtimeConfig] || runtimeConfig.auto
  const AccessIcon = access.icon
  // In the new per-app workspace container, the app code is at /home/coder/workspace/main
  const appFolder = `/home/coder/workspace/main`

  // Permission-based UI control
  const isOwner = app.is_owner ?? true  // Default to true for backwards compatibility
  const myPermission = app.my_permission ?? 'owner'  // Default to owner for backwards compatibility
  const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)
  const canManageShares = isOwner  // Only owner can manage shares
  const canDelete = isOwner  // Only owner can delete
  const canManageSecrets = isOwner || user.isAdmin

  // Permission label for non-owners
  const permissionLabels: Record<string, { label: string; color: string }> = {
    admin: { label: 'Admin Access', color: 'text-purple-500 bg-purple-500/10' },
    edit: { label: 'Edit Access', color: 'text-blue-500 bg-blue-500/10' },
    view: { label: 'View Only', color: 'text-muted-foreground bg-muted' },
    platform: { label: 'Platform Access', color: 'text-muted-foreground bg-muted' },
  }
  const permInfo = !isOwner ? permissionLabels[myPermission] : null

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {app.url ? (
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-2xl font-bold hover:text-primary hover:underline transition-colors"
              >
                {app.display_name || app.name}
              </a>
            ) : (
              <h1 className="text-2xl font-bold">{app.display_name || app.name}</h1>
            )}
            {canEdit && (
              <button
                onClick={() => setShowEditInfo(true)}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
                title="Edit app info"
              >
                <Pencil className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            <InlineCategoryPicker
              currentCategory={app.category || null}
              canEdit={canEdit}
              onCategoryChange={async (cat) => {
                await updateApp(app.name, { category: cat })
                await loadApp()
              }}
            />
            {permInfo && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${permInfo.color}`}>
                {permInfo.label}
              </span>
            )}
          </div>
          <p className="text-muted-foreground font-mono text-sm">/a/{app.name}/</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            displayPhase === 'running' ? (
              <button
                onClick={handleStop}
                disabled={!!actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
              >
                {actionLoading === 'stop' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                Stop
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={!!actionLoading || isDeploying}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'start' || isDeploying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {isDeploying ? 'Deploying...' : 'Start'}
              </button>
            )
          )}
          {canEdit && (
            <button
              onClick={handleRebuild}
              disabled={!!actionLoading || isDeploying}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-500/50 text-orange-500 hover:bg-orange-500/10 transition-colors disabled:opacity-50"
              title="Rebuild container (reinstalls dependencies)"
            >
              {actionLoading === 'rebuild' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Rebuild
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleProductionBuild}
              disabled={!!actionLoading || isDeploying || isProductionBuilding}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition-colors disabled:opacity-50"
              title="Create optimized production build"
            >
              {actionLoading === 'production-build' || isProductionBuilding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Rocket className="w-4 h-4" />
              )}
              {isProductionBuilding ? 'Building...' : 'Production Build'}
            </button>
          )}
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open App
          </a>
        </div>
      </div>

      {actionError && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="p-1 hover:bg-destructive/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Status</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={`w-4 h-4 rounded-full ${status.color}`} />
                {status.pulse && (
                  <div className={`absolute inset-0 w-4 h-4 rounded-full ${status.color} animate-ping`} />
                )}
              </div>
              <div>
                <p className={`font-semibold ${status.textColor}`}>{status.label}</p>
                <p className="text-sm text-muted-foreground">
                  {displayPhase === 'error'
                    ? (errorMessage || app.error_message || 'Deployment failed')
                    : (statusDescriptions[displayPhase] || 'App is not running')}
                </p>
              </div>
            </div>

            {/* Deployment Progress Stepper */}
            {(isDeploying || displayPhase === 'error') && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  {deploymentSteps.map((step, i) => {
                    const stepIndex = deploymentSteps.indexOf(displayPhase as typeof deploymentSteps[number])
                    const errorIndex = displayPhase === 'error'
                      ? deploymentSteps.indexOf(
                          (deploymentPhase && deploymentPhase !== 'error'
                            ? deploymentPhase
                            : app?.deployment_phase && app.deployment_phase !== 'error'
                              ? app.deployment_phase
                              : 'starting') as typeof deploymentSteps[number]
                        )
                      : -1
                    const activeIndex = displayPhase === 'error' ? errorIndex : stepIndex
                    const isComplete = i < activeIndex
                    const isCurrent = i === activeIndex
                    const isError = displayPhase === 'error' && isCurrent

                    return (
                      <div key={step} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            isError ? 'bg-red-500' :
                            isComplete ? 'bg-green-500' :
                            isCurrent ? 'bg-blue-500 animate-pulse' :
                            'bg-gray-600'
                          }`} />
                          <span className={`text-[10px] mt-1 ${
                            isError ? 'text-red-500' :
                            isCurrent ? 'text-foreground font-medium' :
                            isComplete ? 'text-green-500' :
                            'text-muted-foreground'
                          }`}>
                            {step.charAt(0).toUpperCase() + step.slice(1)}
                          </span>
                        </div>
                        {i < deploymentSteps.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-1 ${
                            isComplete ? 'bg-green-500' : 'bg-gray-700'
                          }`} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Error Guidance Panel */}
          {displayPhase === 'error' && (errorMessage || app.error_message) && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">Deployment Error</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {errorMessage || app.error_message}
              </p>
              {(errorDetails?.resolution || app.error_details?.resolution) && (
                <div className="p-3 rounded bg-muted/50">
                  <p className="text-sm font-medium mb-1">How to fix:</p>
                  <p className="text-sm text-muted-foreground">
                    {errorDetails?.resolution || app.error_details?.resolution}
                  </p>
                </div>
              )}
              {(errorDetails?.log_excerpt || app.error_details?.log_excerpt) && (
                <div className="mt-3 p-3 rounded bg-[#1a1a1a] font-mono text-xs max-h-32 overflow-y-auto">
                  <pre className="text-gray-300 whitespace-pre-wrap">
                    {errorDetails?.log_excerpt || app.error_details?.log_excerpt}
                  </pre>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-red-500/10">
                <HelpLink section="deployment">View deployment guide</HelpLink>
              </div>
            </div>
          )}

          {/* Production Status Section */}
          {prodStatus && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Rocket className="w-5 h-5" />
                  Production Build
                </h2>
                <div className="flex items-center gap-2">
                  {prodStatus === 'building' && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-500 animate-pulse">
                      Building...
                    </span>
                  )}
                  {prodStatus === 'running' && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-500">
                      Production Live
                    </span>
                  )}
                  {prodStatus === 'error' && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-500">
                      Build Failed
                    </span>
                  )}
                  {prodStatus === 'stopped' && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400">
                      Production Stopped
                    </span>
                  )}
                </div>
              </div>

              {/* Production building progress */}
              {prodStatus === 'building' && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                  <div>
                    <p className="text-sm font-medium">Building production version...</p>
                    <p className="text-xs text-muted-foreground">This may take a few minutes</p>
                  </div>
                </div>
              )}

              {/* Production running info */}
              {prodStatus === 'running' && (
                <div className="space-y-3">
                  {app.last_successful_build && (
                    <p className="text-sm text-muted-foreground">
                      Last built: {formatRelativeTime(app.last_successful_build)}
                    </p>
                  )}
                  {canEdit && (
                    <button
                      onClick={handleStopProduction}
                      disabled={!!actionLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'production-stop' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Square className="w-3 h-3" />
                      )}
                      Stop Production
                    </button>
                  )}
                </div>
              )}

              {/* Production error */}
              {prodStatus === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-500">
                    {productionError || app.production_error_message || 'Production build failed'}
                  </p>
                  <div className="mt-2">
                    <HelpLink section="deployment">View deployment guide</HelpLink>
                  </div>
                </div>
              )}

              {/* Production stopped */}
              {prodStatus === 'stopped' && canEdit && (
                <button
                  onClick={handleProductionBuild}
                  disabled={!!actionLoading || isProductionBuilding}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition-colors disabled:opacity-50"
                >
                  <Rocket className="w-3 h-3" />
                  Rebuild Production
                </button>
              )}

              {/* Build Logs (expandable) */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => {
                    const newExpanded = !productionLogsExpanded
                    setProductionLogsExpanded(newExpanded)
                    if (newExpanded) {
                      fetchProductionLogs()
                    }
                  }}
                  className="flex items-center justify-between w-full"
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Build Logs
                  </span>
                  <div className="flex items-center gap-2">
                    {productionLogsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    {productionLogsExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                {productionLogsExpanded && (
                  <div className="mt-2 space-y-3">
                    {productionLogs?.build_logs && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Build Output</p>
                        <div className="p-3 rounded-lg bg-[#1a1a1a] font-mono text-xs max-h-48 overflow-y-auto">
                          <pre className="text-gray-300 whitespace-pre-wrap">{productionLogs.build_logs}</pre>
                        </div>
                      </div>
                    )}
                    {productionLogs?.runtime_logs && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Runtime Output</p>
                        <div className="p-3 rounded-lg bg-[#1a1a1a] font-mono text-xs max-h-48 overflow-y-auto">
                          <pre className="text-gray-300 whitespace-pre-wrap">{productionLogs.runtime_logs}</pre>
                        </div>
                      </div>
                    )}
                    {!productionLogs?.build_logs && !productionLogs?.runtime_logs && !productionLogsLoading && (
                      <div className="p-3 rounded-lg bg-[#1a1a1a] font-mono text-xs">
                        <span className="text-gray-500">No production logs available</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {app.description ? (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-2">Description</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{app.description}</p>
            </div>
          ) : canEdit ? (
            <button
              onClick={() => setShowEditInfo(true)}
              className="w-full p-4 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors text-sm"
            >
              + Add a description
            </button>
          ) : null}

          {/* Sessions - only show for users with edit permission */}
          {canEdit && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <SessionsPanel
                appName={app.name}
                onOpenSession={(terminalUrl) => {
                  const workspacePath = withMountBase('/workspace', window.location.pathname)
                  const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
                  const appUrl = `${window.location.origin}${appPath}`
                  window.location.href = `${workspacePath}?app=${encodeURIComponent(app.name)}&mode=terminal&folder=${encodeURIComponent(appFolder)}&url=${encodeURIComponent(appUrl)}&session=${encodeURIComponent(terminalUrl)}`
                }}
              />
            </div>
          )}

          {/* Container Info */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Server className="w-5 h-5" />
              Container
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  displayPhase === 'running' ? 'bg-green-500/20 text-green-500' :
                  isDeploying ? 'bg-amber-500/20 text-amber-500' :
                  displayPhase === 'error' ? 'bg-red-500/20 text-red-500' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {status.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Container Name</span>
                <span className="font-mono text-sm text-primary">cc-app-{app.name}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Port</span>
                <span className="font-mono text-sm">{app.port}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Runtime</span>
                <span className="text-sm font-medium">{runtime.label}</span>
              </div>

              {/* Resource Allocation */}
              {resources && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Cpu className="w-3 h-3" /> CPU
                    </span>
                    <span className="font-mono text-sm">{resources.cpu_limit}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <HardDrive className="w-3 h-3" /> Memory
                    </span>
                    <span className="font-mono text-sm">{resources.memory_limit}</span>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <GitBranch className="w-3 h-3" /> Git
                </span>
                <span className={`text-sm ${app.git_initialized ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {app.git_initialized ? 'Initialized' : 'Not initialized'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Created
                </span>
                <span className="text-sm">{new Date(app.created_at).toLocaleDateString()}</span>
              </div>

              {app.last_accessed && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last Accessed
                  </span>
                  <span className="text-sm">{formatRelativeTime(app.last_accessed)}</span>
                </div>
              )}

              {/* Server Logs with Tabs */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => {
                    setLogsExpanded(!logsExpanded)
                    if (!logsExpanded) {
                      fetchLogs()
                    }
                  }}
                  className="flex items-center justify-between w-full mb-2"
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Server Logs
                  </span>
                  <div className="flex items-center gap-2">
                    {logsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    {logsExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                {logsExpanded && (
                  <>
                    {/* Log Type Tabs */}
                    <div className="flex gap-1 mb-2">
                      {(['all', 'build', 'runtime'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setLogTab(tab)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            logTab === tab
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tab === 'all' ? 'All Logs' : tab === 'build' ? 'Build' : 'Runtime'}
                        </button>
                      ))}
                    </div>
                    <div className="p-4 rounded-lg bg-[#1a1a1a] font-mono text-xs max-h-64 overflow-y-auto">
                      {displayPhase === 'stopped' && !logs ? (
                        <span className="text-gray-500">App is not running</span>
                      ) : logs ? (
                        <pre className="text-gray-300 whitespace-pre-wrap">{logs}</pre>
                      ) : logsLoading ? (
                        <span className="text-gray-500">Loading logs...</span>
                      ) : (
                        <span className="text-gray-500">No logs available</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* App Files */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              App Files
            </h2>
            <FileBrowserPanel
              standalone={true}
              appName={app.name}
            />
          </div>

          {/* Visitor Trends */}
          {appName && <VisitorTrendChart appName={appName} />}

          {/* Sharing */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Sharing</h2>
              {canManageShares && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                <AccessIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{access.label}</p>
                <p className="text-sm text-muted-foreground">{access.description}</p>
              </div>
            </div>
            {shares.length > 0 && canManageShares && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Shared with:</p>
                <div className="space-y-2">
                  {shares.map((share) => (
                    <div key={share.id} className="flex items-center justify-between text-sm">
                      <span>{share.email}</span>
                      <span className="text-muted-foreground">{share.permission}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Runtime</p>
                  <p className="font-medium">{runtime.label}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Git</p>
                  <p className="font-medium">{app.git_initialized ? 'Initialized' : 'Not initialized'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {app.last_accessed && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Last accessed</p>
                    <p className="font-medium">{new Date(app.last_accessed).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* URL */}
          <div className="p-6 rounded-xl border border-border bg-card">
            {prodStatus === 'running' && app.production_url ? (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  URLs
                </h2>
                {/* Production URL (primary) */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-green-500 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                    Production URL
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={app.production_url}
                      readOnly
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                    />
                    <a
                      href={app.production_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      title="Open production URL"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={copyProductionUrl}
                      className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      title="Copy production URL"
                    >
                      {copiedProdUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {/* Dev Preview URL */}
                {app.dev_preview_url && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                      Dev Preview URL <span className="text-xs text-muted-foreground font-normal">(changes visible here)</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={app.dev_preview_url}
                        readOnly
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                      />
                      <a
                        href={app.dev_preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        title="Open dev preview URL"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={copyDevPreviewUrl}
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        title="Copy dev preview URL"
                      >
                        {copiedDevUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-4">App URL</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={app.url}
                    readOnly
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                  />
                  <button
                    onClick={copyUrl}
                    className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                    title="Copy URL"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Resources */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setShowResources(!showResources)}
              className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <h3 className="font-semibold">Resources</h3>
                  <p className="text-sm text-muted-foreground">
                    {resources?.is_customized ? 'Custom configuration' : 'Using defaults'}
                  </p>
                </div>
              </div>
              {showResources ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
            {showResources && resources && (
              <div className="p-4 pt-0 border-t border-border">
                <div className="space-y-3 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Cpu className="w-4 h-4" /> CPU
                    </span>
                    <span>{resources.cpu_request} / {resources.cpu_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <HardDrive className="w-4 h-4" /> Memory
                    </span>
                    <span>{resources.memory_request} / {resources.memory_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Replicas</span>
                    <span>{resources.replicas_min} - {resources.replicas_max}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Disk</span>
                    <span>{resources.disk_gb} GB</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Health Checks</span>
                    <span>{resources.health_check_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Auto Restart</span>
                    <span>{resources.auto_restart ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                {resources.is_customized && (
                  <button
                    onClick={async () => {
                      if (!app) return
                      await resetAppResources(app.name)
                      loadApp()
                    }}
                    className="flex items-center gap-2 mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to defaults
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Keep Alive - Only shown to users with edit permission */}
          {canEdit && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Keep alive</h3>
                    <p className="text-sm text-muted-foreground">
                      Prevent auto-shutdown after inactivity
                    </p>
                  </div>
                </div>
                <Switch
                  checked={app.keep_alive}
                  onCheckedChange={async (checked) => {
                    try {
                      const updated = await updateApp(app.name, { keep_alive: checked })
                      setApp(updated)
                    } catch (err) {
                      console.error('Failed to update keep alive:', err)
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Secrets - only for app owner or platform admin */}
          {canManageSecrets && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                Secrets
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Encrypted per-app secrets. Values are write-only and never shown again.
              </p>
              <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
                <p>Reference these in <code className="font-mono">cc-app.json</code> under <code className="font-mono">env</code>:</p>
                <p><code className="font-mono">MY_TOKEN=${'{secret:MY_TOKEN}'}</code></p>
                <p><code className="font-mono">TLS_CERT_PATH=${'{secret-file:TLS_CERT:/run/secrets/tls-cert.pem}'}</code></p>
              </div>

              {secretError && (
                <div className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
                  {secretError}
                </div>
              )}
              {secretSuccess && (
                <div className="mb-3 p-3 rounded-lg bg-green-500/10 text-green-500 text-sm border border-green-500/20">
                  {secretSuccess}
                </div>
              )}

              <div className="space-y-2 mb-4">
                <input
                  type="text"
                  value={secretNameInput}
                  onChange={(e) => setSecretNameInput(e.target.value)}
                  placeholder="Secret name (e.g. OPENAI_API_KEY)"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="password"
                  value={secretValueInput}
                  onChange={(e) => setSecretValueInput(e.target.value)}
                  placeholder="Secret value"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  value={secretDescriptionInput}
                  onChange={(e) => setSecretDescriptionInput(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSaveSecret}
                  disabled={secretSaving}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {secretSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  Save Secret
                </button>
              </div>

              <div className="pt-4 border-t border-border">
                <h3 className="text-sm font-medium mb-2">Configured secrets</h3>
                {secretsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading secrets...
                  </div>
                ) : appSecrets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No secrets configured for this app.</p>
                ) : (
                  <div className="space-y-2">
                    {appSecrets.map((secret) => (
                      <div key={secret.name} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border">
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-foreground break-all">{secret.name}</p>
                          {secret.description && (
                            <p className="text-xs text-muted-foreground mt-1">{secret.description}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Updated {formatRelativeTime(secret.updated_at || secret.created_at)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteSecret(secret.name)}
                          disabled={secretDeleting === secret.name}
                          className="p-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                          title={`Delete ${secret.name}`}
                        >
                          {secretDeleting === secret.name ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* App Usage Stats - only for owners */}
          {isOwner && appName && <AppUsageStats appName={appName} />}

          {/* Danger Zone - Only shown to owner */}
          {canDelete && (
            <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5">
              <h2 className="text-lg font-semibold mb-4 text-destructive">Danger Zone</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Deleting this app removes it from your active list, but you can restore it for 30 days.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete App
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {/* Edit App Info Modal */}
      {showEditInfo && (
        <EditAppInfoModal
          app={app}
          onClose={() => setShowEditInfo(false)}
          onSaved={(updated) => {
            setShowEditInfo(false)
            if (updated.name !== app.name) {
              navigate(`/apps/${updated.name}`, { replace: true })
            } else {
              setApp(updated)
            }
          }}
        />
      )}

      {showShareModal && (
        <ShareModal
          app={app}
          shares={shares}
          onClose={() => setShowShareModal(false)}
          onShared={loadApp}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Delete "{app.name}"?</h2>
            <p className="text-muted-foreground mb-6">
              This will remove the app from active use and stop its containers. You can restore it for 30 days.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!!actionLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ShareModal({
  app,
  shares,
  onClose,
  onShared,
}: {
  app: App
  shares: AppShare[]
  onClose: () => void
  onShared: () => void
}) {
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState('view')
  const [sharing, setSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPlatformShared, setIsPlatformShared] = useState(app.access_level === 'platform')
  const [platformLoading, setPlatformLoading] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)

  async function handlePlatformToggle() {
    setPlatformLoading(true)
    setError(null)
    try {
      if (isPlatformShared) {
        await unshareFromPlatform(app.name)
        setIsPlatformShared(false)
      } else {
        await shareWithPlatform(app.name)
        setIsPlatformShared(true)
      }
      onShared()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update platform sharing')
    } finally {
      setPlatformLoading(false)
    }
  }

  async function handleShare(e: React.FormEvent) {
    e.preventDefault()
    setSharing(true)
    setError(null)
    try {
      await shareApp(app.name, email, permission)
      setEmail('')
      onShared()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share app')
    } finally {
      setSharing(false)
    }
  }

  async function handleRemove(shareEmail: string) {
    setRemoving(shareEmail)
    try {
      await removeAppShare(app.name, shareEmail)
      onShared()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove share')
    } finally {
      setRemoving(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-1">Share "{app.display_name || app.name}"</h2>
        <p className="text-sm text-muted-foreground mb-6">Control who can access your app</p>

        {/* Platform Sharing Toggle */}
        <div className="p-4 rounded-lg bg-secondary mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isPlatformShared ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">Share with all platform users</p>
                <p className="text-xs text-muted-foreground">
                  {isPlatformShared ? 'Everyone on the platform can view this app' : 'Only you and invited users can access'}
                </p>
              </div>
            </div>
{platformLoading ? (
              <div className="w-11 h-6 rounded-full bg-muted-foreground/30 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : (
              <Switch
                checked={isPlatformShared}
                onCheckedChange={handlePlatformToggle}
              />
            )}
          </div>
        </div>

        <div className="border-t border-border pt-4 mb-4">
          <p className="text-sm font-medium mb-3">Share with specific people</p>
          <form onSubmit={handleShare} className="flex gap-2 mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="view">View</option>
              <option value="edit">Edit</option>
            </select>
            <button
              type="submit"
              disabled={sharing || !email}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </button>
          </form>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
            {error}
          </div>
        )}

        {/* Shares List */}
        <div className="space-y-2 mb-6">
          {shares.length === 0 ? (
            <p className="text-center text-muted-foreground py-4 text-sm">
              No individual shares yet
            </p>
          ) : (
            shares.map((share) => (
              <div
                key={share.id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary"
              >
                <div>
                  <div className="font-medium text-sm">{share.email}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="capitalize">{share.permission}</span>
                    {!share.user_registered && (
                      <span className="text-yellow-500">(pending invite)</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(share.email)}
                  disabled={removing === share.email}
                  className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                >
                  {removing === share.email ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>

        <div className="pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
